export const FIELD_TYPES = [
  { id: "text", label: "Testo" },
  { id: "textarea", label: "Testo lungo" },
  { id: "select", label: "Elenco a scelta" },
  { id: "number", label: "Numero" },
  { id: "date", label: "Data" },
  { id: "checkbox", label: "Casella di spunta" },
  { id: "codice_fiscale", label: "Codice fiscale" },
];

const SLUG_SAFE = /^[a-z][a-z0-9_]{0,63}$/;

export const DEFAULT_REGISTRATION_FIELDS = [
  {
    id: "codice_fiscale",
    label: "Codice fiscale",
    type: "codice_fiscale",
    required: true,
  },
];

export const emptyRegistrationField = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: "",
  label: "",
  type: "text",
  required: true,
  optionsText: "",
});

export function defaultRegistrationFields() {
  return fieldsFromApplication(DEFAULT_REGISTRATION_FIELDS);
}

function slugFromLabel(label) {
  const slug = String(label || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
  if (!slug) return "campo";
  if (/^\d/.test(slug)) return `campo_${slug}`;
  return slug;
}

function uniqueId(candidate, used) {
  let value = candidate || "campo";
  let index = 2;
  while (used.has(value)) {
    const suffix = `_${index}`;
    value = `${(candidate || "campo").slice(0, 64 - suffix.length)}${suffix}`;
    index += 1;
  }
  used.add(value);
  return value;
}

export function parseOptionsText(text) {
  const seen = new Set();
  return String(text || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

export function fieldsFromApplication(fields) {
  if (!Array.isArray(fields) || !fields.length) {
    return [emptyRegistrationField()];
  }
  return fields.map((field) => ({
    key: field.id || emptyRegistrationField().key,
    id: field.id || "",
    label: field.label || "",
    type: field.type || "text",
    required: field.required !== false,
    optionsText: Array.isArray(field.options) ? field.options.join("\n") : "",
  }));
}

export function registrationFieldsPayload(fields) {
  const used = new Set();
  const payload = [];
  (fields || []).forEach((field) => {
    const label = (field.label || "").trim();
    if (!label) return;
    const type = field.type || "text";
    const incomingId = String(field.id || "").trim().toLowerCase();
    const id =
      incomingId && SLUG_SAFE.test(incomingId) && !used.has(incomingId)
        ? (used.add(incomingId), incomingId)
        : uniqueId(slugFromLabel(incomingId || label), used);
    const item = {
      id,
      label,
      type,
      required: Boolean(field.required),
    };
    if (type === "select") {
      item.options = parseOptionsText(field.optionsText);
    }
    payload.push(item);
  });
  return payload;
}

export function validateRegistrationFields(fields) {
  const payload = registrationFieldsPayload(fields);
  const incompleteSelect = payload.find(
    (field) => field.type === "select" && (!field.options || !field.options.length)
  );
  if (incompleteSelect) {
    return `Il campo "${incompleteSelect.label}" è un elenco: indica almeno un'opzione (una per riga).`;
  }
  return "";
}

export function initialValues(fields) {
  const values = {};
  (fields || []).forEach((field) => {
    if (field.type === "checkbox") {
      values[field.id] = false;
    } else if (field.id === "croas" && (field.options || []).includes("Sicilia")) {
      values[field.id] = "Sicilia";
    } else {
      values[field.id] = "";
    }
  });
  return values;
}

export function answersFromUser(user, fields) {
  const values = initialValues(fields);
  const custom = user?.custom_fields && typeof user.custom_fields === "object" ? user.custom_fields : {};
  (fields || []).forEach((field) => {
    if (custom[field.id] != null && custom[field.id] !== "") {
      values[field.id] = custom[field.id];
    } else if (user && user[field.id] != null && user[field.id] !== "") {
      values[field.id] = user[field.id];
    }
  });
  return values;
}

export function applyFieldChange(fields, values, fieldId, value) {
  const next = { ...values, [fieldId]: value };
  if (fieldId === "croas" && (fields || []).some((field) => field.id === "provincia")) {
    if (value !== "Sicilia") next.provincia = "Altra Provincia";
    else if (values.provincia === "Altra Provincia") next.provincia = "";
  }
  return next;
}

export function isFieldDisabled(fields, values, field) {
  if (!field || field.id !== "provincia") return false;
  return (fields || []).some((item) => item.id === "croas") && values.croas !== "Sicilia";
}

export function isOptionDisabled(fields, values, field, option) {
  if (!field || field.id !== "provincia") return false;
  return option === "Altra Provincia" && values.croas === "Sicilia";
}

export function isCodiceFiscaleValid(value) {
  return /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/i.test(
    String(value || "").replace(/\s+/g, "")
  );
}

export function areAnswersValid(fields, values, { codiceFiscaleValid } = {}) {
  return (fields || []).every((field) => {
    const value = values?.[field.id];
    if (field.type === "checkbox") {
      return !field.required || value === true;
    }
    const blank = value == null || String(value).trim() === "";
    if (blank) return !field.required;
    if (field.type === "codice_fiscale") {
      if (typeof codiceFiscaleValid === "function") return codiceFiscaleValid(value);
      return isCodiceFiscaleValid(value);
    }
    if (field.type === "select" && Array.isArray(field.options) && field.options.length) {
      return field.options.includes(value);
    }
    return true;
  });
}
